import React, { useEffect, useState } from 'react';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import api from '@/lib/api';

const ACTION_LABEL = {
  appointment_created: 'Created',
  appointment_updated: 'Updated',
  appointment_deleted: 'Deleted',
};

const ACTION_COLOR = {
  appointment_created: 'success',
  appointment_updated: 'info',
  appointment_deleted: 'error',
};

const FIELD_LABEL = {
  service_id: 'Service',
  member_id: 'Member',
  trainer_id: 'Assigned to',
  site_id: 'Site',
  start_time: 'Start',
  end_time: 'End',
  status: 'Status',
  notes: 'Notes',
  actual_duration_minutes: 'Actual duration',
};

const STATUS_LABEL = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

const fmt = (v, field) => {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'status') return STATUS_LABEL[v] || String(v);
  return String(v);
};

const AppointmentChangelog = ({ appointmentId }) => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/appointments/${appointmentId}/changelog`);
        if (!cancelled) setEntries(data || []);
      } catch (err) {
        if (!cancelled) setEntries([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  if (entries === null) {
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={48} />
      </Stack>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No history recorded yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {entries.map((e) => {
        const changes = e.payload?.changes || {};
        return (
          <Box key={e.audit_id} sx={{ borderLeft: 2, borderColor: 'divider', pl: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                label={ACTION_LABEL[e.action] || e.action}
                size="small"
                color={ACTION_COLOR[e.action] || 'default'}
              />
              <Typography variant="caption" color="text.secondary">
                {e.performer_name || 'Someone'} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
              </Typography>
            </Stack>
            {e.action === 'appointment_updated' &&
              Object.entries(changes).map(([field, val]) => (
                <Typography key={field} variant="body2" color="text.secondary">
                  <strong>{FIELD_LABEL[field] || field}:</strong> {fmt(val.from, field)} → {fmt(val.to, field)}
                </Typography>
              ))}
          </Box>
        );
      })}
    </Stack>
  );
};

export default AppointmentChangelog;
